/**
 * enginePreProcessJson_Merge_Placeholder.ts - Placeholder processing for EnginePreProcessJson
 * Pure ES module - works in both Node.js and browser via bundler
 */

import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { replaceCaseInsensitive } from '../../../../Assembler/dist/common/commonUtil.js';
import { mergeDataJsonIntoHtmlJson } from '../../base/engine/htmlJsonMergeUtil.js';
import {
    getKeys
} from './enginePreProcessJson_Helper.js';
import { TemplateInfoNode, TemplateOperation } from './enginePreProcessJson_Helper.js';
import { TemplateOperationKind } from './enginePreProcessJson_Helper.js';
import {
    TYPE_PLACEHOLDER_SKIP,
    msToNanos,
    getFirstHtmlValue
} from './enginePreProcessJson_Merge_Constants.js';
import type { PreProcessJsonMergeContext } from './enginePreProcessJson_Merge.js';
import type { EnginePreProcessJson, ILoaderPreProcessJson } from './enginePreProcessJson.js';

/**
 * Merge template function signature
 */
type MergeTemplateFn = (
    engine: EnginePreProcessJson,
    loader: ILoaderPreProcessJson,
    context: PreProcessJsonMergeContext,
    templateInfo: TemplateInfoNode,
    htmlJson: JsonObject,
    dataJson: JsonObject,
    operations: TemplateOperation[],
    genOperations: boolean,
    componentHtml: JsonObject | null,
    recursionDepth: number,
    flowStack: string[] | null,
    parentComponentSink?: TemplateOperation[] | null,
    keyReplaceStage?: TemplateOperation[] | null,
    keyMissingStage?: TemplateOperation[] | null,
    parentSlotSink?: TemplateOperation[] | null
) => string;

/**
 * Process placeholder entry in template
 */
export function processPlaceholderEntry(
    engine: EnginePreProcessJson,
    loader: ILoaderPreProcessJson,
    context: PreProcessJsonMergeContext,
    params: {
        key: string;
        keyType: string;
        keyNameFromKey: string;
        value: unknown;
        templateInfo: TemplateInfoNode;
        dataJson: JsonObject;
        operations: TemplateOperation[];
        genOperations: boolean;
        componentHtml: JsonObject | null;
        recursionDepth: number;
        flowStack: string[] | null;
    },
    mergeTemplateFn: MergeTemplateFn
): { result: string; continue: boolean } {
    const {
        keyType,
        keyNameFromKey,
        value,
        templateInfo,
        dataJson,
        operations,
        genOperations,
        componentHtml,
        recursionDepth,
        flowStack
    } = params;
    // AM-001: invariants are read inline from `context` (no local re-bundling) so the old
    // flat appSite/metadata/rootComponent/genComponentHtml/contextJson/configJson parameter
    // group is gone. High-frequency reads alias the few that are not the root component.
    const appSite = context.appSiteName;
    const metadata = context.metadata;
    const genComponentHtml = context.genComponentHtml;
    const contextJson = context.contextJson;
    const configJson = context.configJson;

    // Start timing for placeholder processing
    const startPlaceholder = performance.now();

    // Template placeholder: {{TemplateName}} or {{!$Title}}
    const placeholderValue = value;
    // In new format, extract name from key; fallback to value parsing
    const placeholderName = keyNameFromKey || (typeof value === 'string' ? value.substring(2, value.length - 2) : '');

    // Check if this is a JSON key skip marker ({{!$Key}})
    if (placeholderName.startsWith('!$')) {
        // This is a JSON key skip marker - remove it and log KeySkip operation
        const keyName = placeholderName.substring(2); // Remove "!$" prefix to get "Title"

        // Replace with defaultSkipJson (skip marker)
        const result = loader.defaultSkipJson;

        // Log KeySkip operation
        if (operations && genOperations) {
            const opTemplateName = `${templateInfo.templateAppSite}_${templateInfo.templateName}`;
            operations.push(new TemplateOperation({
                templateName: opTemplateName,
                kind: TemplateOperationKind.KeySkip,
                target: placeholderValue as string,
                replacedWith: `${templateInfo.templateName}_${keyName}`,
                operations: []
            }));
        }

        // Skip timing/count for JsonKey skip markers to align with non-Json engines
        return { result, continue: true };
    }

    // Handle skip-marked placeholders (TYPE_PLACEHOLDER_SKIP) - output DefaultSkipHtml and continue
    // Still call GetTemplate with ! prefix (to match NormalJson GetTemplate count) but skip DynamicView
    if (keyType === TYPE_PLACEHOLDER_SKIP) {
        // First check if this is an AppView placeholder
        let actualComponentName = placeholderName;
        if (context.rootComponent && context.rootComponent.appViewPlaceholders && context.rootComponent.appViewPlaceholders.get(placeholderName)) {
            actualComponentName = context.rootComponent.appViewPlaceholders.get(placeholderName)!;
        }

        // Create immutable copy of flow stack with new item for GetTemplate call (matches NormalJson behavior)
        const newFlowStackSkip = [...(flowStack || []), actualComponentName];

        // Call GetTemplate with ! prefix to skip DynamicView (matches NormalJson behavior for count)
        const startGetSkipComp = performance.now();
        loader.getTemplateWithView(appSite, templateInfo.templateName, '!' + actualComponentName, metadata.appView, newFlowStackSkip, configJson, null, context.clientMode);
        engine._getTemplateNanos += msToNanos(performance.now() - startGetSkipComp);
        engine._getTemplateCount++;

        // For skip-marked placeholders, output defaultSkipHtml directly
        const result = loader.defaultSkipHtml;

        // Add componentHtml entry with ! prefix
        const componentHtmlKey = '!' + placeholderName;
        if (genComponentHtml && componentHtml) {
            componentHtml.set(componentHtmlKey, loader.defaultSkipHtml);
        }

        // Log ComponentSkip operation
        if (operations && genOperations) {
            const opTemplateName = `${templateInfo.templateAppSite}_${templateInfo.templateName}`;
            operations.push(new TemplateOperation({
                templateName: opTemplateName,
                kind: TemplateOperationKind.ComponentSkip,
                target: `{{!${placeholderName}}}`,
                replacedWith: `${templateInfo.templateAppSite}_Skipped`,
                operations: []
            }));
        }

        // Track timing
        engine._placeholderProcessNanos += msToNanos(performance.now() - startPlaceholder);
        engine._placeholderProcessCount++;
        return { result, continue: true };
    }

    let resultStr = '';

    // Create immutable copy of flow stack with new item for inheritance resolution BEFORE GetTemplate call
    // Use placeholderName as actualComponentName (C# approach)
    const actualComponentName = placeholderName;
    const newFlowStack = [...(flowStack || []), actualComponentName];

    // OPTIMIZATION: Single getTemplate call - returns templateInfo for O(1) component lookup
    // getTemplate internally uses component index and handles reuse patterns
    const startGetComp = performance.now();
    const componentResult = loader.getTemplateWithView(appSite, templateInfo.templateName, actualComponentName, metadata.appView, newFlowStack, configJson, null, context.clientMode);
    engine._getTemplateNanos += msToNanos(performance.now() - startGetComp);
    engine._getTemplateCount++;

    // Update newFlowStack with resolved template name for correct inheritance lookup
    const resolvedTemplateName = componentResult.resolvedTemplateName;
    if (resolvedTemplateName && resolvedTemplateName !== actualComponentName && newFlowStack.length > 0) {
        newFlowStack[newFlowStack.length - 1] = resolvedTemplateName;
    }

    const foundComponent = componentResult.templateInfo;

    if (foundComponent) {
        // For reuse components:
        // - HTML cache key uses the actual template name (TemplateName, e.g., ArrayMerge) from TemplateAppSite
        // - JSON cache key uses the placeholder name (PlaceholderName, e.g., MainContent_1_ArrayMerge) from current AppSite
        // This allows reusing the same HTML template with different JSON data
        const isReuseComponent = foundComponent.placeholderName &&
            foundComponent.placeholderName.toLowerCase() !== foundComponent.templateName.toLowerCase();
        // IMPORTANT: AppView fallback can look like reuse (PlaceholderName != TemplateName)
        // but should use TemplateName. Distinguish by checking for reuse pattern (contains _Number_)
        const isRealReuse = isReuseComponent && engine.containsNumberPattern(foundComponent.placeholderName || '');
        // Operation tracking:
        // - Real reuse: use PlaceholderName (full reuse name like "MainContent_1_ArrayMerge")
        // - AppView (isReuseComponent but not isRealReuse): the component index resolved the
        //   placeholder to a file whose name carries filename casing (e.g. viewAContent.html).
        //   Reconstruct the canonical AppView content name the same way the loaders' AppView
        //   fallback does — replaceCaseInsensitive(placeholder, prefix, appView) — so we emit
        //   "ViewAContent" (matching Normal/NormalJson), not "viewAContent". Fall back to
        //   foundComponent.templateName when the placeholder carries no AppView prefix.
        // - Normal: use actualComponentName (placeholder lookup name)
        let templateNameForKey = actualComponentName;
        if (isReuseComponent && !isRealReuse) {
            const appViewPrefix = loader.defaultAppViewPrefix;
            if (metadata.appView && appViewPrefix &&
                actualComponentName.toLowerCase().includes(appViewPrefix.toLowerCase())) {
                templateNameForKey = replaceCaseInsensitive(actualComponentName, appViewPrefix, metadata.appView);
            } else {
                templateNameForKey = foundComponent.templateName;
            }
        }
        const opsTemplateName = isRealReuse ? foundComponent.placeholderName! : templateNameForKey;
        const foundKey = `${foundComponent.templateAppSite}_${opsTemplateName}`;

        if (componentResult.htmlJson && componentResult.htmlJson.size > 0) {
            // Create child operations list for this component
            const childOperations: TemplateOperation[] = [];
            const componentDataJsonRaw: JsonObject = componentResult.dataJson ?? new JsonObject();
            const componentDataJson = componentDataJsonRaw;
            // Merge component's DataJson into HtmlJson using utility (follows architecture)
            // Always merge to replace missing JSON keys with DefaultReplaceHtml
            // For real reuse: use PlaceholderName, for AppView/normal: use templateNameForKey
            const placeholderNameForMerge = isRealReuse ? foundComponent.placeholderName! : templateNameForKey;
            // Stage KeyReplace+KeyMissing so MergeTemplate interleaves them at canonical phase
            // position (after ArrayIteration, before ConditionalBlock).
            const keyReplaceStage: TemplateOperation[] = [];
            const keyMissingStage: TemplateOperation[] = [];
            const startMerge = performance.now();
            const mergedComponentHtmlJson = mergeDataJsonIntoHtmlJson(
                componentResult.htmlJson,
                componentDataJsonRaw,
                foundKey,
                placeholderNameForMerge,
                childOperations,
                loader.defaultReplaceHtml,
                false,
                  contextJson,
                  null,
                  keyReplaceStage,
                  keyMissingStage
              );
            engine._jsonMergeNanos += msToNanos(performance.now() - startMerge);
            engine._jsonMergeCount++;

            // Use component's JSON data if available, otherwise use parent's
            // ComponentHtml key: Use original placeholder name uniformly for consistency across all engines.
            // For reuse patterns: use PlaceholderName as key (e.g., MainContent_1_KeyMerge)
            // For normal components (including AppView): use actualComponentName (placeholder name)

            // Pre-order: Set empty entry BEFORE recursion if genComponentHtml is enabled
            if (genComponentHtml && componentHtml) {
                componentHtml.set(placeholderName, '');
            }

            // Create component info for recursion
            // For reuse patterns, create a modified component with reuse name for ops logging.
            // For the AppView case (isReuseComponent && !isRealReuse), override placeholderName so
            // the recursive template's templateKey carries the canonical {AppView}Content casing
            // (e.g. ViewAContent) rather than the placeholder name (MainContent) — matching NormalJson
            // which threads foundKey (= canonical) as the child templateKey.
            const componentForRecursion = isRealReuse
                ? { ...foundComponent, templateName: opsTemplateName }
                : (isReuseComponent
                    ? { ...foundComponent, placeholderName: templateNameForKey }
                    : foundComponent);

            // Recursively merge component
            const componentMerged = mergeTemplateFn(
                engine,
                loader,
                context,
                componentForRecursion,
                mergedComponentHtmlJson,
                componentDataJson,
                childOperations,
                genOperations,
                componentHtml,
                recursionDepth + 1,
                newFlowStack,
                null,
                keyReplaceStage,
                keyMissingStage,
                null
            );

            resultStr = componentMerged;

            // Post-order: Update entry AFTER recursion if genComponentHtml is enabled
            if (genComponentHtml && componentHtml) {
                componentHtml.set(placeholderName, componentMerged);
            }

            // Log operation
            if (operations && genOperations) {
                // Use the passed-in templateKey (canonical casing from the calling context,
                // i.e. placeholderName when present) rather than reconstructing from
                // templateInfo.templateName (resolved-filename casing). Matches NormalJson.
                const opTemplateName = `${templateInfo.templateAppSite}_${templateInfo.placeholderName || templateInfo.templateName}`;
                operations.push(new TemplateOperation({
                    templateName: opTemplateName,
                    kind: TemplateOperationKind.ComponentReplace,
                    target: placeholderValue as string,
                    replacedWith: foundKey,
                    operations: childOperations
                }));
            }
        }

    } else {
        // Component not found in hierarchy - use data already retrieved from GetTemplate
        // (matches C# behavior - single GetTemplate call, use retrieved values)
        resultStr = processPlaceholderFallback(engine, loader, context, {
            placeholderName,
            placeholderValue: placeholderValue as string,
            templateInfo,
            dataJson,
            operations,
            genOperations,
            componentHtml,
            recursionDepth,
            flowStack: newFlowStack,
            value,
            componentResult
        }, mergeTemplateFn);
    }

    engine._placeholderProcessNanos += msToNanos(performance.now() - startPlaceholder);
    engine._placeholderProcessCount++;

    return { result: resultStr, continue: false };
}

/**
 * Process placeholder fallback when component not found in hierarchy
 * Uses data already retrieved from first GetTemplate call (matches C# behavior)
 */
function processPlaceholderFallback(
    engine: EnginePreProcessJson,
    loader: ILoaderPreProcessJson,
    context: PreProcessJsonMergeContext,
    params: {
        placeholderName: string;
        placeholderValue: string;
        templateInfo: TemplateInfoNode;
        dataJson: JsonObject;
        operations: TemplateOperation[];
        genOperations: boolean;
        componentHtml: JsonObject | null;
        recursionDepth: number;
        flowStack: string[] | null;
        value: unknown;
        componentResult: { htmlJson: JsonObject | null; dataJson: JsonObject | null; resolvedTemplateName: string; resolvedAppSite: string; templateInfo: TemplateInfoNode | null };
    },
    mergeTemplateFn: MergeTemplateFn
): string {
    const {
        placeholderName,
        placeholderValue,
        templateInfo,
        operations,
        genOperations,
        componentHtml,
        recursionDepth,
        flowStack,
        value,
        componentResult
    } = params;
    const appSite = context.appSiteName;
    const genComponentHtml = context.genComponentHtml;
    const contextJson = context.contextJson;

    // Note: flowStack was already pushed/popped in main function, don't manipulate here
    // (matches C# which doesn't re-add to flowStack in the fallback path)

    let resultStr = '';

    if (componentResult && componentResult.htmlJson) {
        const resolvedTemplateName = componentResult.resolvedTemplateName;
        const resolvedComponentAppSite = componentResult.resolvedAppSite || appSite;

        // Determine operation type
        let baseOpType: TemplateOperationKind = TemplateOperationKind.ComponentReplace;
        if (resolvedTemplateName === loader.defaultSkipTemplateName) {
            baseOpType = TemplateOperationKind.ComponentSkip;
        } else if (resolvedTemplateName === loader.defaultReplaceTemplateName) {
            baseOpType = TemplateOperationKind.ComponentMissing;
        }

        const isSkip = resolvedTemplateName === loader.defaultSkipTemplateName;
        const isMissing = resolvedTemplateName === loader.defaultReplaceTemplateName;
        const isRealTemplate = !isSkip && !isMissing && getKeys(componentResult.htmlJson).length >= 1;

        if (isRealTemplate) {
            const childOperations: TemplateOperation[] = [];
            const foundKey = `${resolvedComponentAppSite}_${resolvedTemplateName}`;

            // DynamicView handlers applied after getTemplate above (Area 7: explicit engine call)

            const componentDataJsonRaw: JsonObject = componentResult.dataJson ?? new JsonObject();
            const componentDataJson = componentDataJsonRaw;
            // Stage KeyReplace+KeyMissing so MergeTemplate interleaves them at the canonical
            // phase position (after ArrayIteration). Without staging they land in childOperations
            // ahead of MergeTemplate's ArrayIteration ops, reversing the canonical
            // ArrayIteration→KeyMissing order (mirrors the main foundComponent branch).
            const keyReplaceStage: TemplateOperation[] = [];
            const keyMissingStage: TemplateOperation[] = [];
            const startMergeDefault = performance.now();
            const mergedComponentHtmlJson = mergeDataJsonIntoHtmlJson(
                componentResult.htmlJson,
                componentDataJsonRaw,
                foundKey,
                resolvedTemplateName,
                childOperations,
                loader.defaultReplaceHtml,
                false,
                  contextJson,
                  null,
                  keyReplaceStage,
                  keyMissingStage
              );
            engine._jsonMergeNanos += msToNanos(performance.now() - startMergeDefault);
            engine._jsonMergeCount++;

            const foundTemplateInfo: TemplateInfoNode = {
                templateName: resolvedTemplateName,
                templateAppSite: resolvedComponentAppSite,
                placeholderName: placeholderName,
                components: [],
                jsonInheritKeys: new Map<string, string>(),
                appViewPlaceholders: new Map<string, string>()
            };

            if (genComponentHtml && componentHtml) {
                componentHtml.set(placeholderName, '');
            }

            const merged = mergeTemplateFn(
                engine,
                loader,
                context,
                foundTemplateInfo,
                mergedComponentHtmlJson,
                componentDataJson,
                childOperations,
                genOperations,
                componentHtml,
                recursionDepth + 1,
                flowStack,
                null,
                keyReplaceStage,
                keyMissingStage,
                null
            );

            resultStr = merged;

            if (genComponentHtml && componentHtml) {
                componentHtml.set(placeholderName, merged);
            }

            if (operations && genOperations) {
                // Use the passed-in templateKey casing (placeholderName when present) for
                // canonical parity with NormalJson, not resolved-filename casing.
                const opTemplateName = `${templateInfo.templateAppSite}_${templateInfo.placeholderName || templateInfo.templateName}`;
                operations.push(new TemplateOperation({
                    templateName: opTemplateName,
                    kind: baseOpType,
                    target: placeholderValue,
                    replacedWith: foundKey,
                    operations: childOperations
                }));
            }
        } else {
            const childOperations: TemplateOperation[] = [];
            const defaultHtml = getFirstHtmlValue(componentResult.htmlJson!);
            resultStr = defaultHtml;

            if (genComponentHtml && componentHtml) {
                componentHtml.set(placeholderName, defaultHtml);
            }

            if (operations && genOperations) {
                // Use the passed-in templateKey casing (placeholderName when present) for
                // canonical parity with NormalJson, not resolved-filename casing.
                const opTemplateName = `${templateInfo.templateAppSite}_${templateInfo.placeholderName || templateInfo.templateName}`;
                operations.push(new TemplateOperation({
                    templateName: opTemplateName,
                    kind: baseOpType,
                    target: placeholderValue,
                    replacedWith: `${resolvedComponentAppSite}_${resolvedTemplateName}`,
                    operations: childOperations
                }));
            }
        }
    } else {
        resultStr = value as string;
    }

    // Note: flowStack was already popped in main function before calling this fallback
    // No need to pop here (matches C# behavior)

    return resultStr;
}
